import { Injectable, inject, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { forkJoin, Observable, Subject, timer, of, takeUntil } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment, deployment } from "../../environments/environment";

/**
 * Enum representing the overall availability status of backend services
 */
export enum ServiceStatus {
  AVAILABLE = "AVAILABLE",
  GATEWAY_UNAVAILABLE = "GATEWAY_UNAVAILABLE",
  BACKEND_UNAVAILABLE = "BACKEND_UNAVAILABLE",
  PARTIAL_UNAVAILABLE = "PARTIAL_UNAVAILABLE",
}

/**
 * Interface for tracking health state of individual services
 */
interface ServiceHealthState {
  gateway: boolean;
  backend: boolean;
  dataImporter: boolean;
  lastChecked: number | null;
}

/**
 * Interface for retry configuration
 */
interface RetryConfiguration {
  enabled: boolean;
  interval: number;
  maxAttempts: number;
  currentAttempt: number;
}

@Injectable({
  providedIn: "root",
})
export class BackendHealthService implements OnDestroy {
  private http = inject(HttpClient);
  private router = inject(Router);

  // Health state for each service
  private healthState: ServiceHealthState = {
    gateway: true,
    backend: true,
    dataImporter: true,
    lastChecked: null,
  };

  // Retry configuration
  private retryConfig: RetryConfiguration = {
    enabled: false,
    interval: environment.backendHealth?.retryInterval || 30000,
    maxAttempts: environment.backendHealth?.maxRetryAttempts || (environment.production ? 10 : 3),
    currentAttempt: 0,
  };

  // Timer for automatic retries
  private retryTimer: any = null;

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  // Subject for status changes (for component updates)
  private statusSubject = new Subject<ServiceStatus>();

  // Tracks the last emitted status so a recovery (any unavailable status ->
  // AVAILABLE) can be detected exactly once, to trigger the soft-reload.
  private previousStatus: ServiceStatus = ServiceStatus.AVAILABLE;

  constructor() {
    // Start checking in the background immediately, independent of whether
    // the app happens to make any HTTP calls of its own. Without this, an
    // outage that starts while the user is idle (no in-flight requests to
    // react to) would only ever be detected on the next page reload.
    this.startMonitoring();
  }

  /**
   * Get the overall service status
   */
  getOverallStatus(): ServiceStatus {
    if (!this.healthState.gateway) {
      return ServiceStatus.GATEWAY_UNAVAILABLE;
    }

    if (!this.healthState.backend || !this.healthState.dataImporter) {
      // If backend is down, consider it backend unavailable regardless of data importer
      // If backend is up but data importer is down, it's partial
      if (!this.healthState.backend) {
        return ServiceStatus.BACKEND_UNAVAILABLE;
      }
      return ServiceStatus.PARTIAL_UNAVAILABLE;
    }

    return ServiceStatus.AVAILABLE;
  }

  /**
   * Get observable for status changes
   */
  getStatusObservable(): Observable<ServiceStatus> {
    return this.statusSubject.asObservable();
  }

  /**
   * Check health of all services.
   *
   * This is the actual automatic retry cycle the UI's "Attempt N/max"
   * message refers to, so the retry-attempt counter is owned here: it
   * increments once per check (capped at maxAttempts) while unavailable,
   * and resets once recovered. It must NOT also be incremented by
   * recordError() - that fires once per failed app request, which has no
   * fixed relationship to the retryInterval-paced automatic retry cycle,
   * and previously let the counter run past maxAttempts (e.g. showing
   * "4/3") whenever several requests failed during a single outage.
   */
  checkAllHealth(): Observable<ServiceHealthState> {
    return forkJoin({
      gateway: this.checkGatewayHealth(),
      backend: this.checkBackendHealth(),
      dataImporter: this.checkDataImporterHealth()
    }).pipe(
      map(({ gateway, backend, dataImporter }) => {
        this.healthState = {
          gateway,
          backend,
          dataImporter,
          lastChecked: Date.now(),
        };

        const status = this.getOverallStatus();
        if (status === ServiceStatus.AVAILABLE) {
          this.retryConfig.currentAttempt = 0;
        } else {
          this.retryConfig.currentAttempt = Math.min(
            this.retryConfig.currentAttempt + 1,
            this.retryConfig.maxAttempts
          );
        }
        // The background poll is the single authority allowed to trigger the
        // soft-reload on recovery (see emitStatus): only a genuine outage it
        // has observed warrants tearing down and re-fetching the current view.
        this.emitStatus(status, { allowReload: true });
        return this.healthState;
      })
    );
  }

  /**
   * Check gateway health via actuator endpoint
   */
  checkGatewayHealth(): Observable<boolean> {
    const healthUrl = `${deployment.gatewayPath}${environment.backendHealth?.gatewayHealthPath || "/actuator/health"}`;
    return this.http.get(healthUrl).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Check backend service health via actuator endpoint
   */
  checkBackendHealth(): Observable<boolean> {
    const healthUrl = `${deployment.backendPath}${environment.backendHealth?.backendHealthPath || "/actuator/health"}`;
    return this.http.get(healthUrl).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Check data importer service health via actuator endpoint
   */
  checkDataImporterHealth(): Observable<boolean> {
    const healthUrl = `${deployment.dataImporterPath}${environment.backendHealth?.dataImporterHealthPath || "/actuator/health"}`;
    return this.http.get(healthUrl).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Record an error for a specific URL.
   *
   * Only updates health state for fast, reactive outage detection (an app
   * request can notice an outage sooner than the next scheduled background
   * check). Does NOT touch the retry-attempt counter - see checkAllHealth().
   */
  recordError(url: string, error: any): void {
    // Determine which service based on URL
    // Check more specific paths first (backend, data-importer) before gateway
    // since gatewayPath is a substring of the others
    if (url.includes(deployment.dataImporterPath)) {
      this.healthState.dataImporter = false;
    } else if (url.includes(deployment.backendPath)) {
      this.healthState.backend = false;
    } else if (url.includes(deployment.gatewayPath)) {
      this.healthState.gateway = false;
    }

    const status = this.getOverallStatus();
    this.emitStatus(status);
  }

  /**
   * Record a successful request for a specific URL.
   *
   * Resets the retry-attempt counter immediately once this brings the
   * overall status back to AVAILABLE, so the UI clears without waiting for
   * the next scheduled background check.
   */
  recordSuccess(url: string): void {
    // Determine which service based on URL
    // Check more specific paths first (backend, data-importer) before gateway
    // since gatewayPath is a substring of the others - otherwise every
    // successful backend/data-importer request would be misclassified as a
    // gateway success and healthState.backend/dataImporter would never
    // actually be reset back to true here.
    if (url.includes(deployment.dataImporterPath)) {
      this.healthState.dataImporter = true;
    } else if (url.includes(deployment.backendPath)) {
      this.healthState.backend = true;
    } else if (url.includes(deployment.gatewayPath)) {
      this.healthState.gateway = true;
    }

    const status = this.getOverallStatus();
    if (status === ServiceStatus.AVAILABLE) {
      this.retryConfig.currentAttempt = 0;
    }
    this.emitStatus(status);
  }

  /**
   * Start automatic background health checks, if they aren't already running.
   *
   * Runs for the lifetime of the app (started once from the constructor) so
   * an outage is detected even when nothing else happens to be calling the
   * backend at the time. Checks immediately, then repeats on the configured
   * interval. A no-op when already running: recordError()/recordSuccess()
   * call this too, and restarting the timer here would also wipe out the
   * in-progress retry-attempt counter.
   */
  startMonitoring(): void {
    if (this.retryConfig.enabled && this.retryTimer) {
      return;
    }

    this.retryConfig.enabled = true;

    this.retryTimer = timer(0, this.retryConfig.interval)
      .pipe(
        switchMap(() => this.checkAllHealth()),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Single place all status changes flow through: keeps background
   * monitoring running, publishes the new status to subscribers, and - only
   * when the caller passes options.allowReload (the background health poll
   * does; the reactive recordError/recordSuccess bookkeeping does not) -
   * triggers a soft-reload of the current route on recovery.
   */
  private emitStatus(status: ServiceStatus, options: { allowReload?: boolean } = {}): void {
    // Monitoring is intentionally never stopped just because the status is
    // currently AVAILABLE - a later, new outage still needs to be detected
    // without waiting for another user-triggered request.
    this.startMonitoring();

    // The soft-reload — and the recovery-transition bookkeeping that drives it — belongs
    // solely to the authoritative background health check (checkAllHealth). recordError/
    // recordSuccess react to individual app requests for fast UI feedback, but must NOT
    // reload the whole route: a single transient query failure followed by a retry (or
    // any later success) would otherwise destroy the current view and its in-memory state
    // — the active comparison tab, and the session-only uploaded datasets that cannot be
    // re-fetched. Only a genuine outage confirmed by the health-endpoint poll warrants a
    // reload on recovery. See the per-plot retry in dataset-plot-* (#292).
    if (options.allowReload) {
      if (status === ServiceStatus.AVAILABLE && this.previousStatus !== ServiceStatus.AVAILABLE) {
        this.softReloadCurrentRoute();
      }
      this.previousStatus = status;
    }

    this.statusSubject.next(status);
  }

  /**
   * Re-navigate to whatever route is currently on screen once the backend
   * recovers, so it re-fetches its data instead of staying empty (its
   * initial load most likely failed while the backend/gateway was down).
   *
   * Deliberately not a window.location.reload(): that would throw away the
   * already-loaded app bundle and in-memory session state and cause a full
   * white-page flash. A same-URL router navigation alone isn't enough
   * either - Angular's default RouteReuseStrategy reuses the existing
   * component instance for the identical route, so its ngOnInit() data
   * loading would never re-run. Routing through the (invisible) reload
   * anchor route first forces a real destroy+recreate when navigating back.
   */
  private softReloadCurrentRoute(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl("/__reload__", { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  /**
   * Stop automatic monitoring
   */
  stopMonitoring(): void {
    this.retryConfig.enabled = false;

    if (this.retryTimer) {
      this.retryTimer.unsubscribe();
      this.retryTimer = null;
    }
  }

  /**
   * Trigger manual retry
   */
  triggerManualRetry(): void {
    this.retryConfig.currentAttempt = 0;
    this.checkAllHealth().subscribe();
  }

  /**
   * Get current retry attempt count
   */
  getRetryCount(): number {
    return this.retryConfig.currentAttempt;
  }

  /**
   * Get maximum retry attempts from configuration
   */
  getMaxAttempts(): number {
    return this.retryConfig.maxAttempts;
  }

  /**
   * Get retry interval from configuration
   */
  getRetryInterval(): number {
    return this.retryConfig.interval;
  }

  /**
   * Check if retry limit has been exceeded
   */
  isRetryLimitExceeded(): boolean {
    return this.retryConfig.currentAttempt >= this.retryConfig.maxAttempts;
  }

  /**
   * Check if monitoring is currently enabled
   */
  isMonitoringEnabled(): boolean {
    return this.retryConfig.enabled;
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopMonitoring();
  }
}
